

































































import {
    defineComponent,
    reactive,
    toRefs,
    ref,
    Ref,
    computed,
    PropType,
    watch,
} from '@vue/composition-api';
import Vue from 'vue';
import { assign, cloneDeep } from 'lodash-es';
import DataSet from '@antv/data-set';
import { ChartType, LinkageParamType, ChartSeriesType } from '../type';
import chartTop from './chart-top.vue';
import {
    getReportSeries,
    formatDimension,
    transformDV,
    getStringfyParams,
} from '../service';
import {
    TooltipHtmlContent,
    LegendContainerTpl,
    LegendItemTpl,
    DataLabelConfig,
    CommonChartConfig,
    GameChartConfig,
    DimensionFormatMap,
} from '../const';
import { format, intervalToDuration } from 'date-fns';

export default defineComponent({
    components: {
        chartTop,
    },
    props: {
        chart: {
            type: Object as PropType<ChartType>,
            required: true,
        },
        linkage: {
            type: Object as PropType<LinkageParamType>,
            required: true,
        },
        appId: {
            type: String,
            required: true,
        },
        timeLabel: {
            type: String,
            required: true,
        },
        chartWidth: {
            type: Number,
            default: 1216,
        },
        groupName: {
            type: String,
            required: true,
        },
        isGameTab: {
            type: Boolean,
            default: false,
        },
    },
    setup(props, context) {
        const chartData: Ref<Array<Record<string, any>>> = ref([]);
        const seriesNames: Ref<Array<string>> = ref([]);
        const seriesCaptions: Ref<Array<string>> = ref([]);
        const dataLabel: Ref<Array<any>> = ref([]);
        const renderComponent = ref(true);
        const timeShowType = ref('day');
        const isLoading = ref(false);
        let originData: Array<Record<string, any>> = [];
        const tooltipHtmlContent: Ref<any> = ref({});
        const legendContainerTpl = LegendContainerTpl;
        const legendItemTpl = LegendItemTpl;
        let config: Record<string, any> = props.isGameTab
            ? cloneDeep(GameChartConfig)
            : cloneDeep(CommonChartConfig);
        const series: Ref<Array<ChartSeriesType>> = ref([]);
        config = assign({}, config, props.chart.style);

        const dimensionFormatType = computed(() => {
            return DimensionFormatMap[
                timeShowType.value as keyof typeof DimensionFormatMap
            ].type;
        });
        const dimensionType = computed(() => {
            return DimensionFormatMap[
                timeShowType.value as keyof typeof DimensionFormatMap
            ].dimensionType;
        });

        function transformTimeType(result: Array<Record<string, any>>) {
            let chartSourceData: Array<Record<string, any>> = [];
            chartSourceData = formatDimension(
                dimensionFormatType.value,
                cloneDeep(result),
            );
            chartData.value = transformDV(chartSourceData, seriesNames.value);
        }

        async function getChartData() {
            try {
                isLoading.value = true;
                const linkageParams = {};
                Object.keys(props.linkage).forEach((item) => {
                    if (item !== 'dateRange') {
                        // @ts-ignore
                        linkageParams[item] = props.linkage[item].join(',');
                    }
                });
                const res = await getReportSeries(
                    {
                        source: props.chart.source,
                        appId: props.appId,
                        dimensionType: dimensionType.value,
                        dateRange: props.linkage.dateRange,
                        ...linkageParams,
                    },
                    props.isGameTab,
                );
                series.value = res.series;
                seriesNames.value = series.value.map((item) => item.seriesName);
                seriesCaptions.value = series.value.map(
                    (item) => item.seriesCaption,
                );
                tooltipHtmlContent.value = TooltipHtmlContent(series.value);
                if (res.result !== 1 || !res.data.length) {
                    chartData.value = [];
                    return;
                }
                config.scale[1].min = res.y_axis_min;
                // @ts-ignore
                config.scale[1].max = Math.ceil(res.y_axis_max);
                // 手动调整纵坐标的宽度
                if (res.y_axis_max <= 1000000) {
                    config.padding[3] = 40;
                }
                originData = cloneDeep(res.data);
                // eslint-disable-next-line no-use-before-define
                updateChartData();
            } catch (e) {
                originData = [];
                chartData.value = [];
            } finally {
                isLoading.value = false;
            }
        }

        function forceUpdateComp() {
            renderComponent.value = false;
            setTimeout(() => {
                renderComponent.value = true;
            }, 0);
        }

        function showDataLabel(isShow: boolean) {
            if (!isShow) {
                dataLabel.value = [];
                forceUpdateComp();
                return;
            }
            dataLabel.value = DataLabelConfig;
            forceUpdateComp();
        }

        function updateChartData() {
            const data = cloneDeep(originData);
            if (!data.length) {
                chartData.value = [];
                return;
            }
            transformTimeType(data);
        }

        function refreshTimeOptions() {
            const interval = intervalToDuration({
                start: props.linkage.dateRange[0],
                end: props.linkage.dateRange[1],
            });
            if (
                !props.chart.dimensionUnion &&
                props.chart.dimensionUnion !== undefined
            ) {
                timeShowType.value = 'day';
                return;
            }
            if (props.timeLabel === 'year' || interval.years! > 0) {
                timeShowType.value = 'month';
            } else if (props.timeLabel === 'quarter' || interval.months! > 2) {
                timeShowType.value = 'month';
            } else {
                timeShowType.value = 'day';
            }
        }

        function downloadExcel() {
            const a = document.createElement('a');
            a.href =
                `https://${window.location.host}/portal/data/report/download?appId=${props.appId}&source=${props.chart.source}` +
                `&dateRangeStart=${props.linkage.dateRange[0]}&dateRangeEnd=${props.linkage.dateRange[1]}` +
                `&chartCaption=${props.chart.chartCaption}&groupCaption=${props.groupName}` +
                `&${getStringfyParams(
                    cloneDeep(props.linkage),
                    dimensionType.value,
                )}`;
            a.click();
        }

        watch(
            () => props.linkage,
            async (v) => {
                if (props.appId && v) {
                    refreshTimeOptions();
                    dataLabel.value = [];
                    await getChartData();
                }
            },
            { immediate: true, deep: true },
        );

        const legendItemFormatter = (val: string) => {
            return seriesCaptions.value[seriesNames.value.indexOf(val)];
        };

        return {
            isLoading,
            showDataLabel,
            chartData,
            config,
            tooltipHtmlContent,
            dataLabel,
            renderComponent,
            legendItemFormatter,
            seriesCaptions,
            legendContainerTpl,
            legendItemTpl,
            updateChartData,
            timeShowType,
            getChartData,
            downloadExcel,
        };
    },
});
